/**
 * Auth Error Handler
 * Handles authorization errors by logging out the user
 */

import { supabase } from './supabase'

const ASSISTANT_STORAGE_KEY = 'digimataam_assistant_auth'
const ASSISTANT_RESTAURANT_KEY = 'digimataam_assistant_restaurant'

/**
 * Check if an error is a SESSION INVALIDATION error from the database
 * These are errors that indicate the user's account has been deleted or deactivated
 * and they need to be logged out.
 *
 * NOTE: This should NOT include permission errors like "you can only update your own orders"
 * Those are legitimate permission denials for valid users and should not cause logout.
 */
export function isAuthorizationError(error: any): boolean {
  if (!error) return false

  const errorMessage = error.message || error.toString()

  // Only logout on session invalidation errors (account deleted/deactivated)
  // NOT on permission errors (user is valid but doesn't have permission for specific action)
  return (
    // Account was deleted or deactivated while logged in
    errorMessage.includes('Staff member not found, inactive, or deleted') ||
    errorMessage.includes('inactive or deleted') ||
    // Staff ID doesn't match auth.uid - session hijacking attempt or stale session
    errorMessage.includes('Staff ID does not match authenticated user')
  )
}

/**
 * Check if an error is an authentication required error (400 AUTHENTICATION_REQUIRED)
 * This is typically raised when an assistant session is invalid
 */
export function isAuthenticationRequiredError(error: any): boolean {
  if (!error) return false

  const errorMessage = error.message || error.toString()
  const errorCode = error.code || ''

  return (
    errorMessage.includes('AUTHENTICATION_REQUIRED') ||
    (errorCode === '400' && errorMessage.includes('AUTHENTICATION'))
  )
}

/**
 * Handle authorization errors by logging out the user
 * Should be called when a staff member is deleted or deactivated while logged in
 */
export async function handleAuthorizationError(error: any): Promise<void> {
  if (!isAuthorizationError(error)) {
    return // Not an auth error, don't logout
  }

  console.warn('[Auth Error Handler] Authorization error detected, logging out user:', error.message)

  // Clear staff data from localStorage
  localStorage.removeItem('digimataam_staff_auth')
  localStorage.removeItem('digimataam_staff_restaurant')

  // Sign out from Supabase Auth
  try {
    await supabase.auth.signOut()
  } catch (signOutError) {
    console.error('[Auth Error Handler] Error signing out:', signOutError)
  }

  // Reload the page to redirect to login
  // This ensures all state is cleared and user is redirected properly
  window.location.reload()
}

/**
 * Handle authentication required errors for assistants
 * Should be called when an assistant's session is invalid
 */
export async function handleAssistantAuthenticationError(error: any): Promise<void> {
  if (!isAuthenticationRequiredError(error)) {
    return // Not an authentication error, don't redirect
  }

  console.warn('[Auth Error Handler] Authentication required error detected, redirecting to login:', error.message)

  // Get restaurant ID before clearing storage
  const restaurantId = localStorage.getItem(ASSISTANT_RESTAURANT_KEY)

  // Clear assistant data from localStorage
  localStorage.removeItem(ASSISTANT_STORAGE_KEY)

  // Sign out from Supabase Auth
  try {
    await supabase.auth.signOut()
  } catch (signOutError) {
    console.error('[Auth Error Handler] Error signing out:', signOutError)
  }

  // Redirect to assistant login page with restaurant ID if available
  if (restaurantId) {
    window.location.href = `/assistant-login?restaurant=${restaurantId}`
  } else {
    window.location.href = '/'
  }
}
